import React, { useState } from "react";
import { Icon } from "@iconify/react/dist/iconify.js";

const GarageServices = ({
  categories,
  selectedCategoryId,
  setSelectedCategoryId,
  catalogSubcategories,
  catalogServices,
  catalogLoading,
  selectedServices,
  setSelectedServices,
}) => {
  const [isServicesExpanded, setIsServicesExpanded] = useState(true);
  const [expandedSections, setExpandedSections] = useState({});

  const getServiceSubcategoryId = (service) =>
    service.subcategory?.id ??
    service.subcategory_id ??
    service.subcategoryId ??
    null;

  const directServices = catalogServices.filter(
    (service) => !getServiceSubcategoryId(service),
  );

  const getSubcategoryServices = (subcategoryId) =>
    catalogServices.filter(
      (service) =>
        String(getServiceSubcategoryId(service)) === String(subcategoryId),
    );

  const toggleSection = (sectionId) => {
    setExpandedSections((prev) => ({
      ...prev,
      [sectionId]: !(prev[sectionId] ?? true),
    }));
  };

  const toggleService = (service) => {
    const exists = selectedServices.some(
      (item) => String(item.value) === String(service.id),
    );

    if (exists) {
      setSelectedServices((prev) =>
        prev.filter((item) => String(item.value) !== String(service.id)),
      );
    } else {
      setSelectedServices((prev) => [
        ...prev,
        {
          value: service.id,
          label: service.name,
        },
      ]);
    }
  };

  const isServiceSelected = (serviceId) =>
    selectedServices.some(
      (service) => String(service.value) === String(serviceId),
    );

  const activeCategory = categories.find(
    (category) => String(category.id) === String(selectedCategoryId),
  );

 

  // ---------- NEW: group-wise select all ----------
  const isGroupAllSelected = (services) =>
    services.length > 0 &&
    services.every((service) => isServiceSelected(service.id));

  const handleGroupSelectAll = (services, checked) => {
    if (checked) {
      // Add only this group's services that aren't selected yet
      setSelectedServices((prev) => {
        const existing = new Set(prev.map((item) => String(item.value)));
        const toAdd = services
          .filter((service) => !existing.has(String(service.id)))
          .map((service) => ({ value: service.id, label: service.name }));
        return [...prev, ...toAdd];
      });
    } else {
      // Remove only this group's services
      const groupIds = new Set(services.map((service) => String(service.id)));
      setSelectedServices((prev) =>
        prev.filter((item) => !groupIds.has(String(item.value))),
      );
    }
  };

  const renderGroupSelectAll = (services, id) => (
    <div
      className="form-check style-check d-flex align-items-center gap-2 mb-0 me-3"
      onClick={(e) => e.stopPropagation()} // don't toggle the dropdown
    >
      <input
        className="form-check-input radius-4 border border-neutral-500"
        type="checkbox"
        id={id}
        checked={isGroupAllSelected(services)}
        onChange={(e) => handleGroupSelectAll(services, e.target.checked)}
        disabled={services.length === 0}
      />
      <label
        htmlFor={id}
        className="form-check-label text-sm text-primary-light mb-0 cursor-pointer"
      >
        Select All
      </label>
    </div>
  );
  // ------------------------------------------------

  const renderServiceButtons = (services, emptyMessage) => (
    <div className="p-16 d-flex flex-wrap gap-2">
      {services.length === 0 ? (
        <span className="text-sm text-secondary-light">{emptyMessage}</span>
      ) : (
        services.map((service) => {
          const selected = isServiceSelected(service.id);

          return (
           <button
  key={service.id}
  type="button"
  onClick={() => toggleService(service)}
  style={{ transition: "none" }}
  className={`btn btn-sm radius-8 d-inline-flex align-items-center gap-2 ${
    selected ? "btn-primary-600" : "btn-light border"
  }`}
>
              {service.name}

              {selected && (
                <Icon icon="material-symbols:check" className="text-lg" />
              )}
            </button>
          );
        })
      )}
    </div>
  );

  return (
    <div className="mb-20">
      <label className="form-label fw-semibold text-primary-light text-sm mb-8">
        Services <span className="text-danger-600">*</span>
      </label>

      <div className="card border radius-8 overflow-hidden">
        {/* Category tabs */}
        <div className="card-header bg-base border-bottom p-0">
          <div
            className="d-flex align-items-center gap-2 px-16 py-12 flex-wrap"
            role="tablist"
            aria-label="Service categories"
          >
            {categories.map((category) => {
              const isActive =
                String(selectedCategoryId) === String(category.id);

              return (
                <button
                  key={category.id}
                  type="button"
                  role="tab"
                  aria-selected={isActive}
                  onClick={() => setSelectedCategoryId(String(category.id))}
                  className={`btn btn-sm radius-8 px-16 py-10 ${
                    isActive ? "btn-primary-600" : "btn-light border"
                  }`}
                >
                  {category.name}
                </button>
              );
            })}
          </div>
        </div>

        <div className="card-body p-20 border">
          {/* Main category services toggle */}
          <button
            type="button"
            className=" w-100 d-flex align-items-center justify-content-between mb-3"
            onClick={() => setIsServicesExpanded((prev) => !prev)}
            aria-expanded={isServicesExpanded}
          >
            <span className="d-flex align-items-center gap-2">
              <span className="fw-semibold">
                {activeCategory?.name || "Category Services"}
              </span>

              <span className="badge bg-primary-50 text-primary-600">
                {catalogServices.length}
              </span>
            </span>
            <Icon
              icon={isServicesExpanded ? "mdi:chevron-up" : "mdi:chevron-down"}
              className="text-xl"
            />
          </button>

          {isServicesExpanded && (
            <>
              {catalogLoading ? (
                <div className="text-center py-4 text-secondary-light">
                  Loading services...
                </div>
              ) : categories.length === 0 ? (
                <div className="text-center py-4 text-secondary-light">
                  No categories found.
                </div>
              ) : (
                <>
                  {/* Individual subcategory sections */}
                  {catalogSubcategories.map((subcategory) => {
                    const subServices = getSubcategoryServices(subcategory.id);

                    const sectionId = `subcategory-${subcategory.id}`;
                    const isExpanded = expandedSections[sectionId] ?? true;

                    return (
                      <div
                        key={subcategory.id}
                        className="border radius-8 mb-16 overflow-hidden"
                      >
                        <div
                          role="button"
                          tabIndex={0}
                          className="bg-neutral-50 px-16 py-12 border-bottom w-100 d-flex align-items-center justify-content-between text-start cursor-pointer"
                          onClick={() => toggleSection(sectionId)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              toggleSection(sectionId);
                            }
                          }}
                          aria-expanded={isExpanded}
                        >
                          <span>
                            <span className="d-flex align-items-center gap-2">
                              <span className="fw-semibold text-primary-light">
                                {subcategory.name}
                              </span>

                              <span className="badge bg-primary-50 text-primary-600">
                                {subServices.length}
                              </span>
                            </span>
                            {subcategory.description && (
                              <span className="text-sm text-secondary-light d-block mt-1">
                                {subcategory.description}
                              </span>
                            )}
                          </span>

                          <span className="d-flex align-items-center">
                            {renderGroupSelectAll(
                              subServices,
                              `select-all-${sectionId}`,
                            )}
                            <Icon
                              icon={
                                isExpanded
                                  ? "mdi:chevron-up"
                                  : "mdi:chevron-down"
                              }
                              className="text-xl flex-shrink-0"
                            />
                          </span>
                        </div>

                        {isExpanded &&
                          renderServiceButtons(
                            subServices,
                            "No services available.",
                          )}
                      </div>
                    );
                  })}

                  {/* Direct category services: independent toggle */}
                  <div className="border radius-8 overflow-hidden">
                    <div
                      role="button"
                      tabIndex={0}
                      className="bg-neutral-50 px-16 py-12 border-bottom w-100 d-flex align-items-center justify-content-between text-start cursor-pointer"
                      onClick={() => toggleSection("direct-services")}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          toggleSection("direct-services");
                        }
                      }}
                      aria-expanded={
                        expandedSections["direct-services"] ?? true
                      }
                    >
                      <span>
                        <span className="d-flex align-items-center gap-2">
                          <span className="fw-semibold text-primary-light">
                            Directly under {activeCategory?.name || "Category"}
                          </span>

                          <span className="badge bg-primary-50 text-primary-600">
                            {directServices.length}
                          </span>
                        </span>

                        <span className="text-sm text-secondary-light d-block mt-1">
                          Services without a subcategory
                        </span>
                      </span>

                      <span className="d-flex align-items-center">
                        {renderGroupSelectAll(
                          directServices,
                          "select-all-direct-services",
                        )}
                        <Icon
                          icon={
                            (expandedSections["direct-services"] ?? true)
                              ? "mdi:chevron-up"
                              : "mdi:chevron-down"
                          }
                          className="text-xl flex-shrink-0"
                        />
                      </span>
                    </div>

                    {(expandedSections["direct-services"] ?? true) &&
                      renderServiceButtons(
                        directServices,
                        "No direct services available.",
                      )}
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default GarageServices;